<template>
  <div class="date-presets" ref="root" :class="{ active: isCustom }">
    <button class="presets-btn" type="button" @click.stop="open = !open">
      <span class="presets-label">Период</span>
      <span class="presets-value">{{ currentLabel }}</span>
      <i class="fa-solid fa-chevron-down" :class="{ rotated: open }"></i>
    </button>

    <div v-if="open" class="presets-menu" @click.stop>
      <button
        v-for="preset in PRESETS"
        :key="preset.id"
        class="preset-item"
        type="button"
        :class="{ active: preset.id === 'custom' ? isCustom : preset.id === selected }"
        @click="onSelect(preset)"
      >
        {{ preset.label }}
      </button>
      <template v-if="isCustom || selected === 'custom'">
        <div class="custom-range">
          <input type="date" :value="dateFrom" @change="onCustomFrom" />
          <span>—</span>
          <input type="date" :value="dateTo" @change="onCustomTo" />
        </div>
      </template>
      <template v-if="basis">
        <div class="basis-section">
          <div class="basis-title">Дата-основа периода</div>
          <button
            v-for="option in DATE_BASIS_OPTIONS"
            :key="option.value"
            class="preset-item basis-item"
            type="button"
            :class="{ active: basis === option.value }"
            :title="option.hint"
            @click="$emit('basis-change', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </template>
      <div v-if="timezoneHint" class="tz-hint">
        Период и корзины считаются по местному времени UTC+4 (Ульяновск)
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { DATE_BASIS_OPTIONS } from '@/backend/statisticsApi';
import { toIsoDate } from '../format';

const DAY = 24 * 60 * 60 * 1000;

function iso(dt) {
  return toIsoDate(dt);
}

const PRESETS = [
  { id: 'd7', label: '7 дней' },
  { id: 'd30', label: '30 дней' },
  { id: 'd90', label: '90 дней' },
  { id: 'this_month', label: 'Текущий месяц' },
  { id: 'prev_month', label: 'Прошлый месяц' },
  { id: 'this_quarter', label: 'Текущий квартал' },
  { id: 'this_year', label: 'Текущий год' },
  { id: 'all', label: 'Всё время' },
  { id: 'custom', label: 'Произвольный' },
];

export function presetRange(id) {
  const now = new Date();
  const to = iso(now);
  switch (id) {
    case 'd7':
      return { dateFrom: iso(new Date(now.getTime() - 7 * DAY)), dateTo: to };
    case 'd30':
      return { dateFrom: iso(new Date(now.getTime() - 30 * DAY)), dateTo: to };
    case 'd90':
      return { dateFrom: iso(new Date(now.getTime() - 90 * DAY)), dateTo: to };
    case 'this_month':
      return { dateFrom: iso(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: to };
    case 'prev_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { dateFrom: iso(start), dateTo: iso(end) };
    }
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { dateFrom: iso(new Date(now.getFullYear(), q * 3, 1)), dateTo: to };
    }
    case 'this_year':
      return { dateFrom: iso(new Date(now.getFullYear(), 0, 1)), dateTo: to };
    case 'all':
      return { dateFrom: '', dateTo: '' };
    default:
      return null;
  }
}

export default {
  name: 'DatePresetsDropdown',
  props: {
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
    initialPreset: { type: String, default: 'd90' },
    // Активный пресет от родителя (persist): даты «текущих» пресетов
    // пересчитываются от сегодняшнего дня, поэтому подсвечиваем по id,
    // а не только по совпадению дат.
    presetId: { type: String, default: '' },
    // Дата-основа периода (stat_date | card_create_dt | closed_at):
    // пустая строка — секцию выбора не показывать.
    basis: { type: String, default: '' },
    // Подсказка о часовом поясе корзин/периода (UTC+4)
    timezoneHint: { type: Boolean, default: false },
  },
  emits: ['change', 'basis-change'],
  setup(props, { emit }) {
    const open = ref(false);
    const selected = ref(props.presetId || props.initialPreset);

    // Синхронизация с родителем: приоритет — явный presetId (родитель
    // хранит его в localStorage и ре-применяет при смене дня); иначе
    // подсвечиваем пресет, чей диапазон совпадает с датами, либо custom.
    watch(
      () => [props.presetId, props.dateFrom, props.dateTo],
      ([pid, from, to]) => {
        if (pid) {
          selected.value = pid;
          return;
        }
        const match = PRESETS.find((preset) => {
          if (preset.id === 'custom') return false;
          const range = presetRange(preset.id);
          return range && range.dateFrom === from && range.dateTo === to;
        });
        if (match) {
          selected.value = match.id;
          return;
        }
        const current = presetRange(selected.value);
        if (!current || current.dateFrom !== from || current.dateTo !== to) {
          selected.value = 'custom';
        }
      },
      { immediate: true }
    );

    const isCustom = computed(() => selected.value === 'custom');
    const currentLabel = computed(() => {
      if (isCustom.value) {
        const from = props.dateFrom || '…';
        const to = props.dateTo || '…';
        return `${from} — ${to}`;
      }
      const preset = PRESETS.find((item) => item.id === selected.value);
      return preset ? preset.label : '';
    });

    const emitRange = (range) => {
      emit('change', { dateFrom: range.dateFrom, dateTo: range.dateTo, presetId: selected.value });
    };

    const onSelect = (preset) => {
      selected.value = preset.id;
      if (preset.id === 'custom') return;
      emitRange(presetRange(preset.id) || { dateFrom: '', dateTo: '' });
    };

    const onCustomFrom = (event) => {
      selected.value = 'custom';
      emitRange({ dateFrom: event.target.value, dateTo: props.dateTo });
    };
    const onCustomTo = (event) => {
      selected.value = 'custom';
      emitRange({ dateFrom: props.dateFrom, dateTo: event.target.value });
    };

    const root = ref(null);
    const onDocClick = (event) => {
      if (root.value && !root.value.contains(event.target)) open.value = false;
    };
    onMounted(() => document.addEventListener('click', onDocClick));
    onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

    return {
      PRESETS,
      DATE_BASIS_OPTIONS,
      open,
      root,
      selected,
      isCustom,
      currentLabel,
      onSelect,
      onCustomFrom,
      onCustomTo,
    };
  },
};
</script>

<style scoped>
.date-presets {
  position: relative;
}

.presets-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #1d2939;
}

.presets-btn:hover {
  background: #f8fafc;
}

.date-presets.active .presets-btn {
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.presets-label {
  color: #667085;
}

.presets-value {
  font-weight: 500;
  white-space: nowrap;
}

.presets-btn i {
  font-size: 11px;
  color: #667085;
  transition: transform 0.15s;
}

.presets-btn i.rotated {
  transform: rotate(180deg);
}

.presets-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  min-width: 200px;
  padding: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
}

.preset-item {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  font-size: 13px;
  color: #1d2939;
  cursor: pointer;
}

.preset-item:hover {
  background: #f2f4f7;
}

.preset-item.active {
  background: #eff6ff;
  color: #007bff;
  font-weight: 600;
}

.custom-range {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 6px 4px;
  border-top: 1px solid #f2f4f7;
  margin-top: 4px;
}

.custom-range input {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 12px;
  color: #1d2939;
}

.custom-range span {
  color: #667085;
  font-size: 12px;
}

.basis-section {
  border-top: 1px solid #f2f4f7;
  margin-top: 4px;
  padding-top: 6px;
}

.basis-title {
  padding: 2px 10px 4px;
  font-size: 11px;
  color: #667085;
}

.basis-item {
  padding: 6px 10px;
}

.tz-hint {
  margin-top: 4px;
  padding: 8px 10px;
  border-top: 1px solid #f2f4f7;
  font-size: 11px;
  line-height: 1.4;
  color: #98a2b3;
}
</style>
